import styled, { css } from 'styled-components';

/* ─── Page layout ─── */

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  padding: 0 4px;
`;

export const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  h1 {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.03em;
  }

  span.subtitle {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
  }
`;

/* ─── Stats row ─── */

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

type StatTone = 'brand' | 'info' | 'neutral';
const STAT_TONES: Record<StatTone, { iconBg: string; iconColor: string }> = {
  brand: { iconBg: '#e6f3ec', iconColor: '#006633' },
  info: { iconBg: '#e0ecff', iconColor: '#1d4ed8' },
  neutral: { iconBg: '#f1f5f9', iconColor: '#475569' },
};

export const StatCard = styled.div<{ $tone: StatTone }>`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 14px;

  .icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    ${({ $tone }) => css`
      background: ${STAT_TONES[$tone].iconBg};
      color: ${STAT_TONES[$tone].iconColor};
    `}
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .value {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }

  .helper {
    font-size: 11px;
    color: #64748b;
    font-weight: 500;
  }
`;

/* ─── Cell helpers ─── */

export const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  .avatar {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, #006633 0%, #00874a 100%);
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    letter-spacing: -0.02em;
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .name {
    font-size: 13.5px;
    font-weight: 600;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 260px;
  }

  .email {
    font-size: 11.5px;
    color: #94a3b8;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 260px;
  }
`;

export const MutedCell = styled.span`
  color: #475569;
  font-size: 13px;
`;

export const RoleChip = styled.span<{
  $role: 'admin' | 'syndicate' | 'default';
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;

  ${({ $role }) => {
    if ($role === 'admin')
      return css`
        background: #fff1e0;
        color: #c76a13;
      `;
    if ($role === 'syndicate')
      return css`
        background: #e6f3ec;
        color: #006633;
      `;
    return css`
      background: #f1f5f9;
      color: #475569;
    `;
  }}
`;

export const SyndicateChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  color: #334155;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11.5px;
  font-weight: 600;
  max-width: 220px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    color: #006633;
    flex-shrink: 0;
  }
`;

/* ─── Buttons ─── */

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  border: none;
  background: #006633;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;
  font-family: inherit;

  &:hover:not(:disabled) {
    background: #00451f;
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  &:hover {
    background: #f8fafc;
    border-color: #006633;
    color: #006633;
  }
`;

export const RowAction = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-left: 6px;
  transition: all 0.15s ease;
  font-family: inherit;

  &:first-child {
    margin-left: 0;
  }

  &:hover {
    ${({ $danger }) =>
      $danger
        ? css`
            background: #fef2f2;
            border-color: #b91c1c;
            color: #b91c1c;
          `
        : css`
            background: #f8fafc;
            border-color: #006633;
            color: #006633;
          `}
  }
`;

/* ─── Modal form ─── */

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

export const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #006633;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding-bottom: 4px;
  border-bottom: 1px solid #eef2f7;
`;

export const FieldGroup = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols = 2 }) => $cols}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  input,
  select {
    height: 40px;
    padding: 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 14px;
    font-family: inherit;
    color: #0f172a;
    background: #ffffff;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: #cbd5e1;
    }

    &:focus {
      border-color: #006633;
      box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.12);
    }

    &::placeholder {
      color: #94a3b8;
    }
  }

  .error {
    color: #b91c1c;
    font-size: 12px;
    font-weight: 500;
  }
`;

/* ─── Password field ─── */

export const PasswordShell = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  input {
    flex: 1;
    padding-right: 104px;
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    font-size: 13px;
    letter-spacing: 0.04em;
  }
`;

export const PasswordActions = styled.div`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  gap: 2px;
`;

export const PwdIconButton = styled.button<{ $accent?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${({ $accent }) => ($accent ? '#006633' : '#94a3b8')};
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  &:hover:not(:disabled) {
    background: ${({ $accent }) => ($accent ? '#e6f3ec' : '#f1f5f9')};
    color: ${({ $accent }) => ($accent ? '#006633' : '#334155')};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const StrengthBar = styled.div<{ $level: 0 | 1 | 2 | 3 | 4 }>`
  display: flex;
  gap: 4px;
  margin-top: 6px;

  .segment {
    flex: 1;
    height: 3px;
    border-radius: 999px;
    background: #e2e8f0;
    transition: background 0.2s ease;
  }

  ${({ $level }) => {
    const colors = ['#e2e8f0', '#fecaca', '#fcd9b0', '#c7e4d5', '#006633'];
    const color = colors[$level] || '#e2e8f0';
    return css`
      .segment:nth-child(-n + ${$level}) {
        background: ${color};
      }
    `;
  }}
`;

export const StrengthLabel = styled.span<{ $level: 0 | 1 | 2 | 3 | 4 }>`
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 4px;

  ${({ $level }) => {
    if ($level >= 4)
      return css`
        color: #006633;
      `;
    if ($level === 3)
      return css`
        color: #15803d;
      `;
    if ($level === 2)
      return css`
        color: #c76a13;
      `;
    if ($level === 1)
      return css`
        color: #b91c1c;
      `;
    return css`
      color: #94a3b8;
    `;
  }}
`;

export const CopiedToast = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #006633;
  font-weight: 700;
  margin-left: auto;
`;
